import {Component, OnDestroy, OnInit, ViewChild} from "@angular/core";
import {NgForm} from "@angular/forms";
import {SmartSummaryApiService} from "../../../api/smart-summary-api.service";
import {LoaderService} from "../../services/loader.service";
import {environment} from "../../../../environments/environment";
import {MessagesService} from "../../services/messages.service";


interface Link {
  text: string,
  ged: string
}

interface MarkDownDocument {
  documentTitle: string,
  documentSections: { text: string, links: Link[] }[],
  isFeedbackPresent: boolean
}

@Component({
  selector: 'app-smart-summary',
  templateUrl: './smart-summary.component.html',
  styleUrls: ['./smart-summary.component.scss']
})
export class SmartSummaryComponent implements OnInit, OnDestroy {
  private intervalId: any;
  readonly SMART_SUMMARY_STATES = SMART_SUMMARY_STATES;
  readonly WEBGED_HOST = environment.smartSummaryWebGedHost;
  @ViewChild('smartSummaryForm') smartSummaryForm!: NgForm;
  smartSummaryFormModel: { numSinistro: string } = {numSinistro: ""};

  isFeedbackModalOpen = false;
  feedbackType!: 0 | 1; // 0 Negativo | 1 Positivo
  setFeedbackModalOpen(isOpen: boolean) {
    this.isFeedbackModalOpen = isOpen;
  }

  smartSummaryId!: string;
  idSinistro!: string;

  lastCallsRows: any[] = [];
  lastCallsColumns: string[] = ['numSinistro', 'dataRichiesta', 'stato', 'action'];
  needRefresh = false;

  markDownDocument: MarkDownDocument | undefined = undefined;

  constructor(private smartSummaryApiService: SmartSummaryApiService,
              private loaderService: LoaderService, private messagesService: MessagesService) {
  }

  async ngOnInit() {
    this.loaderService.addLoading();
    await this.populateLastCallsRows();
    this.loaderService.stopLoading();

    this.intervalId = setInterval(() => {
      this.refreshPendingCalls();
    }, 40000);
  }

  ngOnDestroy() {
    if (this.intervalId) {
      clearInterval(this.intervalId);
    }
  }

  resetForm() {
    this.smartSummaryForm.resetForm()
  }

  private async populateLastCallsRows() {
    const ultimeInterrogazioni = await this.smartSummaryApiService.getLastCalls();
    this.lastCallsRows = [];
    let pendingCalls = false;
    for (const interrogazione of ultimeInterrogazioni) {
      pendingCalls = pendingCalls || interrogazione.status === SMART_SUMMARY_STATES.PENDING;
      this.lastCallsRows.push({
        numSinistro: interrogazione.idSinistro,
        dataRichiesta: interrogazione.datetime,
        stato: interrogazione.status,
        smartSummaryId: interrogazione.smartSummaryId
      })
    }
    this.needRefresh = pendingCalls;
  }

  async getSmartSummary() {
    this.loaderService.addLoading();
    await this.smartSummaryApiService.getSmartSummary(this.smartSummaryFormModel.numSinistro, undefined);
    await this.populateLastCallsRows();
    this.loaderService.stopLoading();
  }

  async consulta(numSinistro: string, smartSummaryId: string) {
    const response = await this.smartSummaryApiService.getSmartSummary(numSinistro, smartSummaryId);
    if (response !== null) {
      const newMarkDownDocument: MarkDownDocument = {
        documentTitle: "Sinistro " + numSinistro,
        documentSections: [],
        isFeedbackPresent: response.isFeedbackPresent
      }
      const json = JSON.parse(response.smartSummaryResponse);
      const documento = json.data;

      for (const sezione of documento) {
        let markDown = '';
        const links: Link[] = [];

        markDown += `# ${sezione.section_id}\n`;
        for (const sottoSezione of sezione.data) {
          markDown += `## ${sottoSezione.subsection_id}\n`;
          if (sottoSezione.attributes?.documents?.length > 0) {
            for (const linkSottoSezione of sottoSezione.attributes.documents) {
              links.push({
                text: linkSottoSezione.original_document_type,
                ged: linkSottoSezione.ged_protocol
              })
            }
          }
          markDown += `${sottoSezione.data}\n`
        }

        newMarkDownDocument.documentSections.push({
          text: markDown,
          links: links
        })
      }
      this.smartSummaryId = response.smartSummaryId;
      this.idSinistro = numSinistro;
      this.markDownDocument = newMarkDownDocument;
    }
  }

  closeDetailSection() {
    this.markDownDocument = undefined;
    this.smartSummaryId = "";
    this.idSinistro = "";
  }

  refreshPendingCalls() {
    if (this.needRefresh) {
      this.populateLastCallsRows();
    }
  }

  openFeedbackModal(feedbackType: 0 | 1) {
    this.isFeedbackModalOpen = true;
    this.feedbackType = feedbackType;
  }

  async goToLink(gedProtocol: string) {
    if (!this.smartSummaryId) {
      return;
    }

    try {
      // Chiamata alla tua API Lambda per ottenere il Presigned URL
      const response = await this.smartSummaryApiService.downloadSmartSummaryDocument(gedProtocol, this.smartSummaryId);

      // Estrai l'URL dalla risposta
      const downloadUrl = response.downloadUrl;

      if (!downloadUrl) {
        throw new Error("Download URL not found");
      }

      // Apri l'URL in una nuova scheda
      window.open(downloadUrl, '_blank');
      // Avvia il download utilizzando l'URL
      /*      const a = document.createElement('a');
            a.href = downloadUrl;
            a.download = gedProtocol; // Nome del file che verrà scaricato
            a.click();*/

    } catch (error) {
      this.messagesService.addMessage({text: "Errore durante il recupero del documento", type: "error"});
    }
  }

  formatDate(dateString: string): string {
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${day}/${month}/${year} ${hours}:${minutes}`;
  }
}

enum SMART_SUMMARY_STATES {
  INAVLID = "Invalid",
  PENDING = "Pending",
  COMPLETED = "Completed"
}
